import { useEffect } from "react";
import {
  appendUTMToURL,
  getUTMParams,
  isOutboundToLogcomex,
  mergeParamsIntoURL,
  OUTBOUND_LOGCOMEX_UTMS,
  type UTMParams,
} from "@/lib/utm";

/**
 * Default utm_term based on the CTA's position in the layout.
 * Overridden by an explicit `data-utm-term` on the anchor.
 */
function inferUtmTerm(a: HTMLAnchorElement): string {
  const explicit = a.dataset.utmTerm;
  if (explicit) return explicit;

  // Walk up to find a semantic container.
  const inFooter = a.closest("footer");
  if (inFooter) return "botao-cta-rodape";

  const inHeader = a.closest("header, nav");
  if (inHeader) return "botao-cta-header";

  const inAside = a.closest("aside");
  if (inAside) return "botao-cta-sidebar";

  const inArticle = a.closest("article");
  if (inArticle) return "botao-cta-final-artigo";

  return "botao-cta-blog";
}

/**
 * Global click + submit interceptor.
 *
 * Click rules:
 * 1. Links to logcomex.ai (main site) → apply fixed outbound UTMs
 *    (source=blog, medium=referral, campaign=blog-logcomex-ai) + a per-CTA
 *    utm_term inferred from position or `data-utm-term`. Never overwrite
 *    UTMs already present in the link's own query string.
 * 2. Other links → propagate any saved inbound UTMs from sessionStorage.
 * 3. `data-no-utm` on the anchor disables all UTM handling for it.
 *
 * Submit rules: injects saved inbound UTMs as hidden fields on any form submit.
 */
export function UTMLinkInterceptor() {
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (e.defaultPrevented) return;
      if (e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const target = e.target as HTMLElement | null;
      if (!target) return;
      const anchor = target.closest("a") as HTMLAnchorElement | null;
      if (!anchor) return;
      if (anchor.dataset.noUtm !== undefined) return;

      const rawHref = anchor.getAttribute("href");
      if (!rawHref) return;

      let newHref = rawHref;

      if (isOutboundToLogcomex(rawHref)) {
        const outbound: UTMParams = {
          ...OUTBOUND_LOGCOMEX_UTMS,
          utm_term: inferUtmTerm(anchor),
        };
        newHref = mergeParamsIntoURL(rawHref, outbound);
      } else {
        const utms = getUTMParams();
        if (Object.keys(utms).length === 0) return;
        newHref = appendUTMToURL(rawHref);
      }

      if (newHref !== rawHref) {
        anchor.setAttribute("href", newHref);
      }
    }

    function onSubmit(e: SubmitEvent) {
      const form = e.target as HTMLFormElement | null;
      if (!form || form.dataset.noUtm !== undefined) return;
      const utms = getUTMParams();
      const keys = Object.keys(utms);
      if (keys.length === 0) return;

      for (const k of keys) {
        if (form.querySelector(`input[name="${k}"]`)) continue;
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = k;
        input.value = utms[k as keyof typeof utms] as string;
        form.appendChild(input);
      }
    }

    document.addEventListener("click", onClick, true);
    document.addEventListener("submit", onSubmit, true);
    return () => {
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("submit", onSubmit, true);
    };
  }, []);

  return null;
}

export default UTMLinkInterceptor;
